<script setup lang="ts">
import { isAppId } from '@shared/composables/useAppContext';
// 1:1 port of customer-apps/offering/components/active-wait-list-entries — the app-wide banner
// that announces a wait-list place that just became bookable.
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { formatShortDate, formatShortTime } from '@shared/date-format';
import { getWaitListEntries, waitListEntryIsCurrentlyBookable, waitListRefreshTick } from '@shared/offering';
import type { WaitListEntry } from '@shared/types/offering';
import { useAppStateStore } from '@shared/stores/app-state';
import { useLocalePath } from '@shared/composables/useLocalePath';
import { useOrganizationStore } from '@shared/stores/organization';
import { useRoute } from 'vue-router';

const route = useRoute();
const localePath = useLocalePath();
const appState = useAppStateStore();
const orgStore = useOrganizationStore();

const waitListEntries = ref<WaitListEntry[]>([]);

const THROTTLE_MS = 30000;
const REFRESH_INTERVAL_MS = 180000;

let lastLoadedAt = 0;
let intervalId: ReturnType<typeof setInterval> | null = null;

async function load(force = false) {
    // Angular throttles the navigation-driven reload with `throttleTime(30000)`.
    if (!force && Date.now() - lastLoadedAt < THROTTLE_MS) {
        return;
    }
    lastLoadedAt = Date.now();
    try {
        const entries = await getWaitListEntries();
        waitListEntries.value = entries.filter((entry) => waitListEntryIsCurrentlyBookable(entry));
    } catch {
        waitListEntries.value = [];
    }
}

// Angular: `router.events → NavigationEnd → throttleTime(30000) → getWaitListEntries()`; the first
// NavigationEnd arrives right after bootstrap, so the initial load happens on mount here.
watch(() => route.fullPath, () => void load());

// BookableOfferingService's constructor: `merge(appStateService.show, interval(180000))` — the
// `show` stream is a BehaviorSubject and therefore also fires on subscribe.
watch(
    () => appState.isVisible,
    (visible) => {
        if (visible) {
            void load(true);
        }
    },
);

// A cancelled booking or wait-list entry pushes into `refreshWaitListEntries$` in Angular.
watch(waitListRefreshTick, () => void load(true));

onMounted(() => {
    void load(true);
    // Angular gates the timer tick on `appStateService.isVisible.value === true`, so a backgrounded
    // app makes no requests.
    intervalId = setInterval(() => {
        if (appState.isVisible) {
            void load(true);
        }
    }, REFRESH_INTERVAL_MS);
});

onUnmounted(() => {
    if (intervalId) {
        clearInterval(intervalId);
    }
});

// Angular `'offerings' | appContextRoute`: club-offering prefixes the organization id, every other
// app uses the plain path.
const offeringsBasePath = computed(() =>
    isAppId('club_offering') ? `${orgStore.organization?.id}/offerings` : 'offerings',
);

function bookingLink(waitListEntry: WaitListEntry) {
    const segment = waitListEntry.offeringType === 'course' ? 'courses' : 'generic-offerings';
    return {
        path: localePath(`${offeringsBasePath.value}/${segment}`),
        query: {
            ...route.query,
            appointmentSeriesId: waitListEntry.appointmentSeriesId,
            start: waitListEntry.appointmentStart,
        },
    };
}
</script>

<template>
    <div class="activeWaitListEntries">
        <div
            v-for="waitListEntry in waitListEntries"
            :key="waitListEntry.id"
            class="kgr-alert kgr-alert--info kgr-alert--fullWidth kgr-row kgr-row--noneIfLastChild"
        >
            <div class="kgr-headline kgr-headline--h4 kgr-headline--text kgr-headline--firstInText">
                {{ $t('Warteliste') }}: {{ waitListEntry.appointmentTitle }}
            </div>
            <div class="kgr-row">
                <div class="kgr-row">
                    <i18n-t
                        keypath="Es ist ein Platz für {fullName} frei geworden."
                        tag="span"
                    >
                        <template #fullName>
                            <b class="kgr-text kgr-text--bold">{{ waitListEntry.member.fullName }}</b>
                        </template>
                    </i18n-t>
                </div>

                <i18n-t
                    keypath="Platz buchen bis {date} {time} Uhr, anschließend verfällt der Wartelistenplatz."
                    tag="span"
                >
                    <template #date>
                        <b class="kgr-text kgr-text--bold">{{ formatShortDate(waitListEntry.reservedUntil) }}</b>
                    </template>
                    <template #time>
                        <b class="kgr-text kgr-text--bold">{{ formatShortTime(waitListEntry.reservedUntil) }}</b>
                    </template>
                </i18n-t>
            </div>
            <RouterLink
                :to="bookingLink(waitListEntry)"
                class="kgr-button kgr-button--accent"
            >
                {{ $t('zur Angebotsbuchung') }}
            </RouterLink>
        </div>
    </div>
</template>

<style scoped>
.activeWaitListEntries {
    display: block;
}
</style>
